import { Link } from 'react-router-dom';

const stages = [
  {
    id: 'rg', title: '1001旋轉門', english: 'Revolving Gate', tree: '/images/trilogy/rg-tree.jpg',
    gate: '/images/trilogy/rg-gate.jpg',
    lead: '從生命樹出發，在生活與工作中整合 1D–5D，守住中軸，建立入世成功的能力。',
    individual: ['三根系與四軸', '本心與五秒智慧', '1D–5D 生命能力', 'Seed i'],
    collective: ['在關係與工作中實踐', '創新價值與自主承擔'],
    ending: '走向黃色旋轉門｜完成築基，邁向下一階段',
    link: '/revolving-gate', linkText: '閱讀《1001旋轉門》',
  },
  {
    id: 'og', title: '1001一之門', english: 'Oneness Gate', tree: '/images/trilogy/og-tree.jpg',
    gate: '/images/trilogy/og-gate.jpg',
    lead: '從淡紫生命樹出發：個體錨定一之軸理，同時在子場、母場共同建造生命場域。',
    individual: ['5–5.5D 與 HEB1', '成所作智與小 i', '宇炁與 HEB123', '衷愛軸'],
    collective: ['子場與母場共建', '生命供應與創新價值', '走向人間天堂'],
    ending: '走向紫色一之門｜6.5D 是培育與覆核的節點',
    link: '/og/portal', linkText: '認識一之門 Portal', missionLink: '/mission/human-paradise', missionText: '母場人間天堂使命',
  },
  {
    id: 'ow', title: '1001宇之光', english: 'Light of the Cosmos', tree: '/images/trilogy/ow-tree.jpg',
    gate: '/images/trilogy/ow-gate.jpg',
    lead: '從淺藍綠生命樹出發：建造宇光之塔接通宇宙源光，個體成為宇光之柱的馨真人，聯網建造宇宙燈塔與父場，行星成為發光的星球，走向星際文明的創造。',
    individual: ['宇光之柱', '宇宙燈塔', '宇神同功'],
    collective: ['宇光之塔與 7.5D 父場', '燈塔聯網與星際文明', '創新價值、創造文明'],
    ending: '走向藍綠色門｜9D、證果與祕軸開啟屬於後續覆核路徑',
    link: '/mission/light-towers', linkText: '父場宇光之塔使命', missionLink: null, missionText: null,
  },
] as const;

export default function TrilogySection() {
  return (
    <section id="trilogy" className="bg-[#faf8f5] scroll-mt-20" aria-labelledby="trilogy-heading">
      <div className="mx-auto max-w-5xl px-5 py-16 text-center md:py-20">
        <p className="text-xs tracking-[0.25em] text-foreground-500">THE THREE-PART JOURNEY</p>
        <h2 id="trilogy-heading" className="mt-4 text-2xl md:text-4xl" style={{ fontFamily: 'var(--font-heading)' }}>從生命樹出發，逐段走向門</h2>
        <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-foreground-600 md:text-base">
          三部曲各從同一棵生命樹的不同階段出發。書籍提供地圖，Portal 陪伴實踐；個體生命與集體場域共同成熟，門是每一階段的目標，而非起點。
        </p>
      </div>
      {stages.map((stage) => (
        <article key={stage.id} id={`trilogy-${stage.id}`} className="scroll-mt-20 border-t border-black/5">
          <div className="relative isolate flex min-h-[420px] items-end overflow-hidden md:min-h-[560px]">
            <img src={stage.tree} alt={`${stage.title}階段的生命樹`} loading="lazy" className="absolute inset-0 -z-20 h-full w-full object-cover object-center" />
            <div className="absolute inset-0 -z-10 bg-gradient-to-t from-white/95 via-white/50 to-white/5" />
            <div className="mx-auto w-full max-w-5xl px-5 pb-10 pt-24 text-center md:pb-14">
              <p className="text-xs tracking-[0.22em] text-foreground-700">從生命樹出發 · {stage.english}</p>
              <h3 className="mt-3 text-3xl text-foreground-900 md:text-5xl" style={{ fontFamily: 'var(--font-heading)' }}>{stage.title}</h3>
              <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-foreground-800 md:text-lg">{stage.lead}</p>
            </div>
          </div>
          <div className="mx-auto grid max-w-5xl gap-5 px-5 py-10 md:grid-cols-2 md:py-14">
            <div className="rounded-2xl border border-black/10 bg-white/85 p-6 md:p-8">
              <p className="text-xs tracking-[0.18em] text-foreground-500">個體生命</p>
              <ul className="mt-4 flex flex-wrap gap-2">{stage.individual.map(item => <li key={item} className="rounded-full border border-black/10 bg-white px-3 py-1.5 text-sm text-foreground-800">{item}</li>)}</ul>
            </div>
            <div className="rounded-2xl border border-black/10 bg-white/85 p-6 md:p-8">
              <p className="text-xs tracking-[0.18em] text-foreground-500">集體場域與文明</p>
              <ul className="mt-4 flex flex-wrap gap-2">{stage.collective.map(item => <li key={item} className="rounded-full border border-black/10 bg-white px-3 py-1.5 text-sm text-foreground-800">{item}</li>)}</ul>
            </div>
            <p className="text-center text-sm leading-7 text-foreground-600 md:col-span-2">書籍與 Portal 實作 → 留下 Evidence → 依各階段標準覆核；付費或點擊不代表自動通過。</p>
          </div>
          <div className="relative isolate flex min-h-[340px] items-end overflow-hidden md:min-h-[450px]">
            <img src={stage.gate} alt={`${stage.title}階段的門`} loading="lazy" className="absolute inset-0 -z-20 h-full w-full object-cover object-center" />
            <div className="absolute inset-0 -z-10 bg-gradient-to-t from-white/95 via-white/40 to-transparent" />
            <div className="mx-auto w-full max-w-5xl px-5 pb-8 text-center md:pb-12">
              <p className="text-base text-foreground-900 md:text-xl" style={{ fontFamily: 'var(--font-heading)' }}>{stage.ending}</p>
              {stage.link ? <Link to={stage.link} className="mt-5 inline-block rounded-md bg-foreground-900 px-6 py-3 text-sm text-white hover:bg-foreground-700">{stage.linkText}</Link>
                : <p className="mt-3 text-xs text-foreground-600">本階段內容持續建構中，尚未開放培育與資格申請。</p>}
              {'missionLink' in stage && stage.missionLink ? <Link to={stage.missionLink} className="ml-3 mt-5 inline-block rounded-md border border-foreground-500/40 bg-white/80 px-6 py-3 text-sm text-foreground-900">{stage.missionText}</Link> : null}
            </div>
          </div>
        </article>
      ))}
    </section>
  );
}
